import type { ArmMetrics } from '../../types/api';
import { parseIsoDate, toIsoDate } from '../calendar/calendarDates';

/**
 * Plain functions behind the admin Learning tab: the default window, its
 * limits, arm order and labels, and how the 0..1 ratios read. No React, no API.
 */

/** Days the tab opens on, matching the endpoint's own default. */
export const DEFAULT_WINDOW_DAYS = 30;

/** Longest window the endpoint accepts; longer is a 422. */
export const MAX_WINDOW_DAYS = 180;

/** What a missing number reads as. */
export const NOT_AVAILABLE = 'n/a';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface LearningRange {
  since: string;
  until: string;
}

/** The last 30 days, ending today, as local `YYYY-MM-DD` dates. */
export function defaultLearningRange(today: Date = new Date()): LearningRange {
  const since = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  since.setDate(since.getDate() - DEFAULT_WINDOW_DAYS);
  return { since: toIsoDate(since), until: toIsoDate(today) };
}

/** Whole days from `since` to `until`; NaN when either is not a date. */
function spanDays(range: LearningRange): number {
  const since = parseIsoDate(range.since).getTime();
  const until = parseIsoDate(range.until).getTime();
  return Math.round((until - since) / DAY_MS);
}

/** Why a window cannot be asked for, or null when it can. */
export function learningRangeError(range: LearningRange): string | null {
  if (!range.since || !range.until) return 'Pick both dates.';
  const days = spanDays(range);
  if (Number.isNaN(days)) return 'Pick both dates.';
  if (days < 0) return 'The start date is after the end date.';
  if (days > MAX_WINDOW_DAYS) return `Pick a window of ${MAX_WINDOW_DAYS} days or less.`;
  return null;
}

const ARM_ORDER: readonly ArmMetrics['arm'][] = ['memory', 'holdout', 'off', 'unknown'];

const ARM_LABELS: Record<ArmMetrics['arm'], string> = {
  memory: 'With memory',
  holdout: 'Held out',
  off: 'Switched off',
  unknown: 'Before tracking',
};

export function armLabel(arm: ArmMetrics['arm']): string {
  return ARM_LABELS[arm] ?? arm;
}

/** Arms in a fixed order (memory, holdout, off, unknown), whatever the server sent. */
export function sortArms(arms: readonly ArmMetrics[]): ArmMetrics[] {
  return [...arms].sort((a, b) => rank(a.arm) - rank(b.arm));
}

function rank(arm: ArmMetrics['arm']): number {
  const index = ARM_ORDER.indexOf(arm);
  return index === -1 ? ARM_ORDER.length : index;
}

/** Drops a trailing ".0" / ".00" so 50 reads as 50, not 50.0. */
function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/** A 0..1 share as a percentage: 0.615 is "61.5%". Null is "n/a". */
export function formatShare(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return NOT_AVAILABLE;
  return `${trimZeros((ratio * 100).toFixed(1))}%`;
}

/** A per-request rate: 0.8 is "0.8", 0.333 is "0.33". Null is "n/a". */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_AVAILABLE;
  return trimZeros(value.toFixed(2));
}

/** The holdout setting as the tab states it. */
export function holdoutLabel(pct: number): string {
  return pct > 0 ? `${pct}% of new requests held out` : 'Off (0%)';
}
